import React, { useMemo, useState } from 'react';
import { Card, Table, Form, Badge, Pagination, Button } from 'react-bootstrap';

const formatMoeda = (val) =>
  val != null
    ? val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatData = (val) =>
  val ? new Date(val).toLocaleDateString('pt-BR') : '—';

const renderizarPaginacao = (pagina, totalPaginas, onMudarPagina) => {
  if (totalPaginas <= 1) return null;
  const max = 5;
  const inicio = Math.max(1, Math.min(pagina - Math.floor(max / 2), totalPaginas - max + 1));
  const fim = Math.min(totalPaginas, inicio + max - 1);
  const itens = [];

  itens.push(
    <Pagination.First
      key="first"
      disabled={pagina === 1}
      onClick={() => onMudarPagina(1)}
    />
  );
  itens.push(
    <Pagination.Prev
      key="prev"
      disabled={pagina === 1}
      onClick={() => onMudarPagina(pagina - 1)}
    />
  );
  if (inicio > 1) itens.push(<Pagination.Ellipsis key="el-left" disabled />);
  for (let i = inicio; i <= fim; i += 1) {
    itens.push(
      <Pagination.Item key={`pg-${i}`} active={i === pagina} onClick={() => onMudarPagina(i)}>
        {i}
      </Pagination.Item>
    );
  }
  if (fim < totalPaginas) itens.push(<Pagination.Ellipsis key="el-right" disabled />);
  itens.push(
    <Pagination.Next
      key="next"
      disabled={pagina === totalPaginas}
      onClick={() => onMudarPagina(pagina + 1)}
    />
  );
  itens.push(
    <Pagination.Last
      key="last"
      disabled={pagina === totalPaginas}
      onClick={() => onMudarPagina(totalPaginas)}
    />
  );

  return itens;
};

// Célula editável inline para Parcelas ou Valor
const CelulaEditavel = ({ valor, tipo, onChange }) => {
  const [editando, setEditando] = useState(false);
  const [valorLocal, setValorLocal] = useState(valor);

  const handleBlur = () => {
    setEditando(false);
    const parsed = tipo === 'parcelas' ? parseInt(valorLocal, 10) : parseFloat(valorLocal);
    if (!isNaN(parsed)) onChange(parsed);
    else setValorLocal(valor);
  };

  if (editando) {
    return (
      <Form.Control
        type="number"
        value={valorLocal}
        onChange={(e) => setValorLocal(e.target.value)}
        onBlur={handleBlur}
        autoFocus
        style={{ width: tipo === 'parcelas' ? '70px' : '110px' }}
        min={tipo === 'parcelas' ? 1 : 0}
        step={tipo === 'parcelas' ? 1 : 0.01}
      />
    );
  }

  return (
    <span
      style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
      title="Clique para editar"
      onClick={() => {
        setValorLocal(valor);
        setEditando(true);
      }}
    >
      {tipo === 'parcelas' ? valor : formatMoeda(valor)}
    </span>
  );
};

const GridImoveis = ({
  imoveis,
  selecionados,
  onToggle,
  onToggleTodos,
  onEditarParcelas,
  onEditarValor,
  onAbrirParametros,
  mostrarBotaoParametros,
  textoBotaoParametros,
}) => {
  const [pagina, setPagina] = useState(1);
  const tamanhoPagina = 25;
  const listaImoveis = imoveis || [];
  const totalPaginas = Math.max(1, Math.ceil(listaImoveis.length / tamanhoPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const imoveisPagina = useMemo(
    () => listaImoveis.slice((paginaAtual - 1) * tamanhoPagina, paginaAtual * tamanhoPagina),
    [listaImoveis, paginaAtual]
  );

  if (listaImoveis.length === 0) {
    return (
      <p className="text-muted text-center my-3">
        Nenhum imóvel encontrado. Use os filtros acima para buscar.
      </p>
    );
  }

  const todosChecked = listaImoveis.length > 0 && listaImoveis.every((i) => selecionados.has(i.codigo_boleto));
  const inicio = ((paginaAtual - 1) * tamanhoPagina) + 1;
  const fim = Math.min(paginaAtual * tamanhoPagina, listaImoveis.length);

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span className="text-muted small">
          {selecionados.size} de {listaImoveis.length} selecionado(s) · exibindo {inicio}-{fim}
        </span>
        <div>
          {mostrarBotaoParametros && (
            <Button
              variant="success"
              size="sm"
              className="mr-2"
              onClick={onAbrirParametros}
            >
              {textoBotaoParametros || 'Parâmetros de Lançamento'}
            </Button>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => onToggleTodos(!todosChecked)}
          >
            {todosChecked ? 'Desmarcar Todos' : 'Selecionar Todos'}
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table striped bordered hover size="sm" className="mb-0">
            <thead className="thead-light">
              <tr>
                <th style={{ width: '36px' }}>
                  <Form.Check
                    type="checkbox"
                    checked={todosChecked}
                    onChange={(e) => onToggleTodos(e.target.checked)}
                    title="Selecionar todos"
                  />
                </th>
                <th>Imóvel</th>
                <th>QD</th>
                <th>LT</th>
                <th>Boleto</th>
                <th>Área m2</th>
                <th>Data Venda</th>
                <th>Parcelas</th>
                <th>Valor por Lote</th>
                <th>R$ Parcela</th>
                <th>Próx.Venc.</th>
                <th>Primeiro venc.</th>
                <th>Chave</th>
                <th>GE</th>
                <th>Cliente</th>
                <th>Apelido</th>
              </tr>
            </thead>
            <tbody>
              {imoveisPagina.map((item) => (
                <tr
                  key={item.codigo_boleto}
                  className={selecionados.has(item.codigo_boleto) ? 'table-primary' : ''}
                >
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selecionados.has(item.codigo_boleto)}
                      onChange={() => onToggle(item.codigo_boleto)}
                    />
                  </td>
                  <td>
                    <span className={item.tem_iptu === 'S' ? 'text-danger font-weight-bold' : ''}>
                      {item.codigo_ativo}
                    </span>
                    {item.tem_iptu === 'S' && (
                      <Badge variant="warning" className="ml-1">IPTU</Badge>
                    )}
                  </td>
                  <td>{item.quadra}</td>
                  <td>{item.lote}</td>
                  <td>{item.codigo_boleto}</td>
                  <td>{item.area_total != null ? `${Number(item.area_total).toFixed(2)}` : '—'}</td>
                  <td>{formatData(item.data_venda)}</td>
                  <td>
                    <CelulaEditavel
                      valor={item.parcelas}
                      tipo="parcelas"
                      onChange={(v) => onEditarParcelas(item.codigo_boleto, v)}
                    />
                  </td>
                  <td>{formatMoeda(item.valor_iptu_lote)}</td>
                  <td>
                    <CelulaEditavel
                      valor={item.valor_parcela}
                      tipo="valor"
                      onChange={(v) => onEditarValor(item.codigo_boleto, v)}
                    />
                  </td>
                  <td>{formatData(item.proximo)}</td>
                  <td>{item.primeiro || '—'}</td>
                  <td>{item.chave && item.chave.trim() ? item.chave : '—'}</td>
                  <td>{item.ge}</td>
                  <td>{item.codigo_cliente}</td>
                  <td>{item.apelido || item.cliente}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
      {totalPaginas > 1 && (
        <Card.Footer className="d-flex justify-content-center">
          <Pagination size="sm" className="mb-0">
            {renderizarPaginacao(paginaAtual, totalPaginas, setPagina)}
          </Pagination>
        </Card.Footer>
      )}
    </Card>
  );
};

export default GridImoveis;
